/**
 * Authentication Context for global auth state management
 */
import { createContext, useContext, useState, useEffect } from 'react';
import {
  login as loginService,
  logout as logoutService,
  isAuthenticated,
  getCurrentUser,
  saveCurrentUser,
  clearCurrentUser,
} from '../services/authService';

const AuthContext = createContext(null);

/**
 * Hook to use auth context
 * @returns {Object} Auth context value
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Auth Provider component
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthChecked, setIsAuthChecked] = useState(false);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = () => {
      if (isAuthenticated()) {
        const currentUser = getCurrentUser();
        setUser(currentUser);
      }
      setLoading(false);
      setIsAuthChecked(true);
    };

    checkAuth();
  }, []);

  /**
   * Login user
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} User data
   */
  const login = async (email, password) => {
    try {
      const { user: userData } = await loginService(email, password);
      setUser(userData);
      saveCurrentUser(userData);
      return userData;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      await logoutService();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      clearCurrentUser();
    }
  };

  /**
   * Check if user has a specific permission
   * @param {string} module - Permission module
   * @param {string} action - Permission action
   * @returns {boolean}
   */
  const hasPermission = (module, action) => {
    if (!user || !user.permissions) {
      return false;
    }
    const permKey = `${module}:${action}`;
    return user.permissions.includes(permKey);
  };

  /**
   * Update user data
   * @param {Object} userData - Updated user data
   */
  const updateUser = (userData) => {
    setUser(userData);
    saveCurrentUser(userData);
  };

  const value = {
    user,
    loading,
    isAuthChecked,
    isAuthenticated: !!user,
    login,
    logout,
    hasPermission,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContext;
